import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import AuthenticatedFileButton from '../../components/AuthenticatedFileButton';
import {
  bodyTextClass, dangerTextButtonClass, labelTextClass, primaryButtonClass,
} from './shared';

export type PrestacaoPcArquivo = { id: string; tipo: string; nomeOriginal: string; bemId?: string | null };

type Props = {
  pcId: string;
  /** FOTO_EXECUCAO | FOTO_BEM | REGISTRO_FUNCIONAL | CONTRATO_ALUGUEL */
  tipo: string;
  /** Só para FOTO_BEM: o bem a que o arquivo pertence. */
  bemId?: string;
  podeEditar: boolean;
  /** Atributo accept do input (a validação de verdade é do servidor). */
  accept?: string;
  botao?: string;
  multiplos?: boolean;
  onChange?: () => void;
};

/**
 * Anexos de uma PC que não são extrato (fotos, registro funcional, contrato de aluguel).
 * Usa o mesmo endpoint dos extratos; filtra por tipo (e por bem) no cliente.
 */
const PrestacaoPcArquivos: React.FC<Props> = ({
  pcId, tipo, bemId, podeEditar, accept, botao = 'Anexar', multiplos = false, onChange,
}) => {
  const base = `/prestacao-contas/pcs/${pcId}/extratos`;
  const [arquivos, setArquivos] = useState<PrestacaoPcArquivo[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const res = await api.get(base);
    const todos: PrestacaoPcArquivo[] = Array.isArray(res.data) ? res.data : [];
    setArquivos(todos.filter((a) => a.tipo === tipo && (!bemId || a.bemId === bemId)));
  }, [base, tipo, bemId]);

  useEffect(() => {
    let ativo = true;
    setErro(null);
    api.get(base)
      .then((res) => {
        if (!ativo) return;
        const todos: PrestacaoPcArquivo[] = Array.isArray(res.data) ? res.data : [];
        setArquivos(todos.filter((a) => a.tipo === tipo && (!bemId || a.bemId === bemId)));
      })
      .catch(() => ativo && setErro('Falha ao carregar os arquivos.'));
    return () => { ativo = false; };
  }, [base, tipo, bemId]);

  const anexar = async () => {
    if (!file) return;
    setOcupado(true);
    setErro(null);
    try {
      const data = new FormData();
      data.append('tipo', tipo);
      if (bemId) data.append('bemId', bemId);
      data.append('file', file);
      await api.post(base, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      setFileKey((k) => k + 1);
      await carregar();
      onChange?.();
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Falha ao anexar o arquivo.');
    } finally {
      setOcupado(false);
    }
  };

  const excluir = async (id: string) => {
    if (!confirm('Excluir este arquivo?')) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.delete(`${base}/${id}`);
      await carregar();
      onChange?.();
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Falha ao excluir o arquivo.');
    } finally {
      setOcupado(false);
    }
  };

  const podeAnexar = podeEditar && (multiplos || arquivos.length === 0);

  return (
    <div className="space-y-2">
      {arquivos.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3">
          <AuthenticatedFileButton url={`${base}/${a.id}/view`} fileName={a.nomeOriginal} className={`text-sm underline ${bodyTextClass}`}>
            {a.nomeOriginal}
          </AuthenticatedFileButton>
          {podeEditar && (
            <button type="button" disabled={ocupado} onClick={() => excluir(a.id)} className={`text-xs ${dangerTextButtonClass}`}>
              Excluir
            </button>
          )}
        </div>
      ))}
      {arquivos.length === 0 && !podeEditar && <p className={`text-xs ${labelTextClass}`}>Nenhum arquivo anexado.</p>}
      {podeAnexar && (
        <div className="flex flex-wrap items-center gap-3">
          <input
            key={fileKey}
            type="file"
            accept={accept}
            disabled={ocupado}
            className={`min-w-0 text-sm ${bodyTextClass}`}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <button type="button" disabled={ocupado || !file} onClick={anexar}
            className={`rounded-xl px-3 py-2 text-sm disabled:opacity-50 ${primaryButtonClass}`}>
            {ocupado ? 'Enviando...' : botao}
          </button>
        </div>
      )}
      {erro && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{erro}</p>}
    </div>
  );
};

export default PrestacaoPcArquivos;
